import { useState } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { Download } from 'react-feather';

import { CsvOutputSelectModal } from './CsvOutputSelectModal';
import { useOaCsvRequest } from '../../hooks/Download/useOACsvDownload';
import { useSelectiveCsvRequest } from '../../hooks/Download/useCsvSelectedDownload';
import { useSelectedOrCurrentYear } from '../../hooks/useYears';
import { components } from '../../schema/data-crud';

type Props =
  | {
      filterType: 'jianId';
      selectedJianIds: string[];
    }
  | {
      filterType: 'search';
      target: 'jian' | 'dispatchInfo' | 'patientInfo';
      searchConditions: components['schemas']['SearchConditions'];
      disabled?: boolean;
    };

export const CsvOutputMenuButton: React.FC<Props> = (props) => {
  const [isOpenModal, setIsOpenModal] = useState(false);
  const { request: requestOaCsv, isPending: isPendingOaCsv } = useOaCsvRequest();
  const { request: requestSelectiveCsv, isPending: isPendingSelectiveCsv } =
    useSelectiveCsvRequest();
  const year = useSelectedOrCurrentYear();
  const disabled = props.filterType === 'search' && (props.disabled ?? false);

  return (
    <>
      <Menu as="div" className="relative inline-block">
        <MenuButton className="flex items-center gap-2 px-2 py-1 bg-white rounded text-darkblue_100 disabled:text-darkblue_30" disabled={disabled}>
          <Download size={20} className="" />
          <span className="font-bold">ダウンロード</span>
        </MenuButton>
        <MenuItems className="absolute left-0 mt-1 w-72 border-2 border-darkblue_100 bg-white rounded py-1 z-50">
          <Item
            label="国表CSV出力"
            description="国表をCSV形式で出力します"
            pending={isPendingOaCsv}
            onClick={() => {
              if (props.filterType === 'jianId') {
                if (props.selectedJianIds.length === 0) {
                  window.alert('1件以上の事案をチェックボックスで選択してください');
                  return;
                }
                requestOaCsv({
                  targetJian: {
                    filteredType: 'jianId',
                    year,
                    jianIds: props.selectedJianIds,
                  },
                });
              }
              if (props.filterType === 'search') {
                console.log(props.searchConditions);
                if (props.searchConditions.length === 0) {
                  window.alert('検索条件を指定してください');
                  return;
                }
                requestOaCsv({
                  targetJian: {
                    filteredType: 'search',
                    year,
                    searchCondition: {
                      target: props.target,
                      conditions: props.searchConditions,
                    },
                  },
                });
              }
            }}
          />
          <Item
            label="CSV選択出力"
            description="出力項目を選択しCSV形式で出力します"
            onClick={() => {
              if (props.filterType === 'jianId') {
                if (props.selectedJianIds.length === 0) {
                  window.alert('1件以上の事案をチェックボックスで選択してください');
                  return;
                }
              }
              setIsOpenModal(true);
            }}
          />
        </MenuItems>
      </Menu>
      <CsvOutputSelectModal
        isOpen={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        submit={(targetFieldIds) => {
          if (props.filterType === 'jianId') {
            requestSelectiveCsv({
              targetJian: { filteredType: 'jianId', year, jianIds: props.selectedJianIds },
              targetFieldIds,
            });
          }
          if (props.filterType === 'search') {
            requestSelectiveCsv({
              targetJian: {
                filteredType: 'search',
                year,
                searchCondition: { target: props.target, conditions: props.searchConditions },
              },
              targetFieldIds,
            });
          }
        }}
        pending={isPendingSelectiveCsv}
      />
    </>
  );
};

const Item: React.FC<{
  label: string;
  description: string;
  onClick?: () => void;
  pending?: boolean;
}> = ({ label, description, onClick, pending }) => {
  return (
    <MenuItem>
      <button
        className={`w-full text-left px-3 py-2 space-y-1 border-b last:border-none border-darkblue_10 hover:bg-darkblue_10 ${
          pending ? 'cursor-wait' : 'cursor-pointer '
        }`}
        onClick={onClick}
        disabled={pending}
      >
        <p className="font-bold text-lg text-darkblue_100">{label}</p>
        <p className="text-sm text-darkblue_70">{description}</p>
      </button>
    </MenuItem>
  );
};
